// ===========================================
// Parent Dashboard Page
// ===========================================
// Dashboard for parents with Overview and Student-specific tabs

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  Divider,
  Skeleton,
  Tab,
  Tabs,
  Button,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Dashboard as OverviewIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  ViewCompact as CompactIcon,
  ViewAgenda as ExpandedIcon,
  EventBusy as AbsenceIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import { StatWidget } from '../../components/dashboard/StatWidget';
import {
  FamilyUpcomingSchedule,
  NotificationsCard,
  StudentHeader,
} from '../../components/dashboard';
import {
  RecentNotes,
  RecentResources,
  StudentUpcomingClasses,
  StudentViewDashboard,
  StudentPiecesCard,
} from '../../components/dashboard/parent';
import {
  getAgeGroupFromBirthDate,
  getCharacterColor,
  AgeGroup,
} from '../../components/brand';
import { useAuth } from '../../contexts/useAuth';
import { useTenant } from '../../contexts/TenantContext';
import { useStudentView } from '../../contexts/useStudentView';
import { useMyBookings, useMyCalendarEvents } from '../../hooks/useHybridBooking';
import { useNotesByStudent } from '../../hooks/useNotes';
import { useResourcesByStudent } from '../../hooks/useResources';
import { useMyChildren } from '../../hooks/useParentProfile';
import { useMakeupEligibilities } from '../../hooks/useBooking';
import {
  useParentNotifications,
  useDismissNotification,
} from '../../hooks/useDashboard';
import { useParentInvoices, useParentFamilyCredit } from '../../hooks/useInvoices';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { useViewTerms } from '../../hooks/useViewTerms';
import { parseUTCDate } from '../../utils/dateUtils';
import ParentEventsWidget from '../../components/parent/ParentEventsWidget';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function ParentDashboardPage() {
  const { user } = useAuth();
  const { schoolSlug } = useTenant();
  const navigate = useNavigate();
  const { isStudentView, selectedStudent: studentViewStudent } = useStudentView();

  // Get students for this parent (using parent's family)
  const { data: myStudents = [], isLoading: studentsLoading } = useMyChildren();

  // Tab state: 'overview' or student ID
  // In student view mode, always show the selected student's tab
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Compact stats toggle (persisted in localStorage)
  const [compactStats, setCompactStats] = useState<boolean>(() => {
    const saved = localStorage.getItem('parentDashboard.compactStats');
    return saved === 'true';
  });

  const toggleCompactStats = () => {
    setCompactStats((prev) => {
      const newValue = !prev;
      localStorage.setItem('parentDashboard.compactStats', String(newValue));
      return newValue;
    });
  };

  // In student view mode, force the tab to be the selected student
  useEffect(() => {
    if (isStudentView && studentViewStudent) {
      setActiveTab(studentViewStudent.id);
    }
  }, [isStudentView, studentViewStudent]);

  // Set initial tab when data loads
  useEffect(() => {
    if (activeTab === 'overview' && myStudents.length === 0) {
      // Keep on overview
    }
  }, [activeTab, myStudents]);

  // Selected student (for student tabs) - use context data in student view mode (has updated avatar)
  const selectedStudent = useMemo(() => {
    if (isStudentView && studentViewStudent) {
      // Use context data which is updated immediately when avatar changes
      return studentViewStudent;
    }
    if (activeTab === 'overview') return null;
    return myStudents.find((s) => s.id === activeTab) || null;
  }, [activeTab, myStudents, isStudentView, studentViewStudent]);

  // Queries - use parent-accessible calendar API instead of admin lessons API
  const { data: calendarEvents = [], isLoading: calendarLoading } = useMyCalendarEvents();
  const { data: bookingsData = [], isLoading: bookingsLoading } = useMyBookings();
  const { data: invoicesData = [], isLoading: invoicesLoading } = useParentInvoices();
  // #642 Phase 2: the family's account credit, named on the Outstanding stat.
  const { data: familyCredit } = useParentFamilyCredit();
  const creditOnAccount = familyCredit ? Number(familyCredit.balance) : 0;
  const { data: notifications = [], isLoading: notificationsLoading } = useParentNotifications();
  // Not /admin/terms: a parent cannot read it, and the Notes term filter came up empty (#272).
  const { data: terms = [] } = useViewTerms();
  const { data: makeupEligibilities = [] } = useMakeupEligibilities();
  const dismissNotification = useDismissNotification();

  const totalMakeupCreditsRemaining = useMemo(
    () => makeupEligibilities.filter((e) => !e.redeemedAt).length,
    [makeupEligibilities]
  );

  const notificationsWithMakeup = useMemo(() => {
    if (totalMakeupCreditsRemaining <= 0) return notifications;
    const creditCount = totalMakeupCreditsRemaining;
    const synthetic = {
      id: 'MAKEUP_CREDIT_available',
      type: 'MAKEUP_CREDIT' as const,
      title: creditCount === 1 ? '1 makeup credit available' : `${creditCount} makeup credits available`,
      message: 'Tap to book a replacement lesson',
      targetId: 'available',
      navigateTo: '/parent/bookings',
      createdAt: new Date().toISOString(),
    };
    return [synthetic, ...notifications];
  }, [notifications, totalMakeupCreditsRemaining]);

  // Student-specific queries (only when on student tab)
  const { data: notesData = [], isLoading: notesLoading } = useNotesByStudent(
    selectedStudent?.id || ''
  );
  const { data: resourcesData = [], isLoading: resourcesLoading } = useResourcesByStudent(
    selectedStudent?.id || ''
  );

  // Calculate family stats from calendar events
  const todayLessonsCount = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return calendarEvents.filter((event) => {
      const eventDate = new Date(event.start);
      return eventDate >= today && eventDate < tomorrow &&
        event.resource.type !== 'HYBRID_PLACEHOLDER' &&
        // A school event (concert, camp) is not a lesson (#256)
        event.resource.type !== 'EVENT';
    }).length;
  }, [calendarEvents]);

  // Count unique lesson enrollments from calendar events
  const totalEnrollments = useMemo(() => {
    const uniqueLessons = new Set<string>();
    calendarEvents.forEach((event) => {
      if (event.resource.lessonId && event.resource.type !== 'HYBRID_PLACEHOLDER') {
        // Use lessonId + studentName to get unique enrollments
        const key = `${event.resource.lessonId}_${event.resource.studentName || 'unknown'}`;
        uniqueLessons.add(key);
      }
    });
    return uniqueLessons.size;
  }, [calendarEvents]);

  const upcomingBookingsCount = useMemo(() => {
    return bookingsData.filter(
      (b) => b.status !== 'CANCELLED' && parseUTCDate(b.scheduledDate) >= new Date()
    ).length;
  }, [bookingsData]);

  const unpaidInvoices = useMemo(
    () =>
      invoicesData.filter(
        (i) => i.status === 'SENT' || i.status === 'PARTIALLY_PAID' || i.status === 'OVERDUE'
      ),
    [invoicesData]
  );
  const totalOutstanding = useMemo(
    () =>
      unpaidInvoices.reduce(
        (sum, i) => sum + (parseFloat(i.total) - parseFloat(i.amountPaid)),
        0
      ),
    [unpaidInvoices]
  );
  const overdueCount = useMemo(
    () => invoicesData.filter((i) => i.status === 'OVERDUE').length,
    [invoicesData]
  );

  // Handle notification actions
  const handleDismissNotification = (type: string, targetId: string) => {
    dismissNotification.mutate({ type, targetId });
  };

  const handleNavigateNotification = (notification: { navigateTo: string }) => {
    const path = schoolSlug ? `/${schoolSlug}${notification.navigateTo}` : notification.navigateTo;
    navigate(path);
  };

  const isLoading = studentsLoading || calendarLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  if (myStudents.length === 0) {
    return (
      <Box>
        <PageHeader
          title="Parent Dashboard"
          subtitle={`Welcome${user?.firstName ? `, ${user.firstName}` : ''}!`}
        />
        <Alert severity="info">
          No students found in your family. Please contact the school to enroll your children.
        </Alert>
      </Box>
    );
  }

  // Student view mode shows simplified dashboard for the student
  if (isStudentView && selectedStudent) {
    // Calculate character color based on avatar or age
    const avatarCharacterMap: Record<string, AgeGroup> = {
      alice: 'PRESCHOOL',
      steve: 'KIDS',
      liam: 'TEENS',
      floyd: 'ADULT',
    };
    const ageGroup: AgeGroup = selectedStudent.birthDate
      ? getAgeGroupFromBirthDate(selectedStudent.birthDate)
      : 'KIDS';
    const avatarCharacter = selectedStudent.avatar?.split('/')[0] || '';
    const avatarAgeGroup = avatarCharacterMap[avatarCharacter] || ageGroup;
    const characterColor = getCharacterColor(avatarAgeGroup);

    return (
      <StudentViewDashboard
        student={selectedStudent}
        characterColor={characterColor}
        ageGroup={ageGroup}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title="Parent Dashboard"
        subtitle={`Welcome back${user?.firstName ? `, ${user.firstName}` : ''}!`}
      />

      {/* Tab Navigation: Overview | Student 1 | Student 2 | ... */}
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="overview"
          label="Overview"
          icon={<OverviewIcon />}
          iconPosition="start"
        />
        {myStudents.map((student) => (
          <Tab
            key={student.id}
            value={student.id}
            label={student.firstName}
            icon={<PersonIcon />}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {/* Stats Row - Same for all tabs */}
      <Box sx={{ mb: 3 }}>
        {/* Toggle button for compact/expanded view */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title={compactStats ? 'Expand stats' : 'Compact stats'}>
            <IconButton
              size="small"
              onClick={toggleCompactStats}
              sx={{ color: 'text.secondary' }}
            >
              {compactStats ? <ExpandedIcon fontSize="small" /> : <CompactIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={compactStats ? 1 : 2}>
          <Grid item xs={compactStats ? 6 : 6} sm={compactStats ? 4 : 4} md={compactStats ? 2 : 2}>
            <StatWidget
              title="Calendar"
              value={todayLessonsCount}
              icon={<CalendarIcon />}
              color="primary"
              subtitle="Classes today"
              loading={calendarLoading}
              href={schoolSlug ? `/${schoolSlug}/parent/calendar` : 'calendar'}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={compactStats ? 6 : 6} sm={compactStats ? 4 : 4} md={compactStats ? 2 : 2}>
            <StatWidget
              title="Enrolments"
              value={totalEnrollments}
              icon={<SchoolIcon />}
              color="secondary"
              subtitle="Active classes"
              loading={calendarLoading}
              href={schoolSlug ? `/${schoolSlug}/parent/enrolments` : 'enrolments'}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={compactStats ? 6 : 6} sm={compactStats ? 4 : 4} md={compactStats ? 2 : 2}>
            <StatWidget
              title="Bookings"
              value={upcomingBookingsCount}
              icon={<EventIcon />}
              color="info"
              subtitle="Upcoming"
              loading={bookingsLoading}
              href={schoolSlug ? `/${schoolSlug}/parent/bookings` : 'bookings'}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={compactStats ? 6 : 6} sm={compactStats ? 4 : 4} md={compactStats ? 2 : 2}>
            <StatWidget
              title="Outstanding"
              value={`$${Math.round(totalOutstanding)}`}
              icon={<AttachMoneyIcon />}
              color={overdueCount > 0 ? 'error' : totalOutstanding > 0 ? 'warning' : 'success'}
              subtitle={
                overdueCount > 0
                  ? `${overdueCount} overdue`
                  : creditOnAccount > 0
                    ? `${formatCurrencyFromDollars(creditOnAccount)} credit`
                    : undefined
              }
              loading={invoicesLoading}
              href={schoolSlug ? `/${schoolSlug}/parent/invoices` : 'invoices'}
              compact={compactStats}
              stackLabel={compactStats && totalOutstanding > 0}
            />
          </Grid>
          <Grid item xs={compactStats ? 6 : 6} sm={compactStats ? 4 : 4} md={compactStats ? 2 : 2}>
            <StatWidget
              title="Notifications"
              value={notifications.length}
              icon={<DescriptionIcon />}
              color="success"
              subtitle="New items"
              loading={notificationsLoading}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={compactStats ? 6 : 6} sm={compactStats ? 4 : 4} md={compactStats ? 2 : 2}>
            <StatWidget
              title="Students"
              value={myStudents.length}
              icon={<PersonIcon />}
              color="info"
              subtitle="In family"
              compact={compactStats}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Content based on active tab */}
      {activeTab === 'overview' ? (
        // OVERVIEW TAB
        <Grid container spacing={3}>
          {/* Upcoming Schedule - 14 days for all students */}
          <Grid item xs={12} lg={8}>
            <FamilyUpcomingSchedule
              students={myStudents}
              calendarEvents={calendarEvents}
              daysAhead={14}
              isLoading={calendarLoading}
              maxItems={20}
            />
          </Grid>

          {/* Notifications */}
          <Grid item xs={12} lg={4}>
            <NotificationsCard
              notifications={notificationsWithMakeup}
              isLoading={notificationsLoading}
              onDismiss={handleDismissNotification}
              onNavigate={handleNavigateNotification}
            />

            {/* Quick Actions */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<AbsenceIcon />}
                    component={Link}
                    to="report-absence"
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Report Absence
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EventIcon />}
                    component={Link}
                    to="bookings"
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage Bookings
                  </Button>
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<PaymentIcon />}
                    component={Link}
                    to="invoices"
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    View Invoices
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Upcoming Events widget */}
            <Box sx={{ mt: 2 }}>
              <ParentEventsWidget />
            </Box>
          </Grid>
        </Grid>
      ) : selectedStudent ? (
        // STUDENT TAB
        <Box>
          {/* Student Header */}
          <StudentHeader student={selectedStudent} />

          <Grid container spacing={3}>
            {/* Student's Upcoming Classes */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {selectedStudent.firstName}'s Schedule
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <StudentUpcomingClasses
                    studentName={`${selectedStudent.firstName} ${selectedStudent.lastName}`}
                    calendarEvents={calendarEvents}
                    isLoading={calendarLoading}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Student's Class Notes */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Recent Class Notes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <RecentNotes
                    notes={notesData}
                    isLoading={notesLoading}
                    studentName={selectedStudent.firstName}
                    terms={terms}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Student's Pieces */}
            <Grid item xs={12} md={6}>
              <StudentPiecesCard
                studentId={selectedStudent.id}
                studentName={selectedStudent.firstName}
              />
            </Grid>

            {/* Student's Resources */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Class Resources
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <RecentResources
                    resources={resourcesData}
                    isLoading={resourcesLoading}
                    onViewAll={() => navigate('resources')}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : null}
    </Box>
  );
}
